import React, { Component, Fragment } from "react";
import { AppCard } from "../../components/ui";

import { TransitionGroup, CSSTransition } from "react-transition-group";
import moment from "moment";
import { Toast } from "primereact/toast";
import { utils } from "../../utils/utils";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Column } from "primereact/column";
import {
    stringFilterMatchModeOptions,
    numberFilterMatchModeOptions,
    dateFilterTemplate
} from "../Paginator";
import { issuingApiService } from "../../services/api/Issuing/IssuingApiService";
import { CardEvent } from "../../models/IssuingClientsModel";
import { Fieldset } from "primereact/fieldset";

interface ClientEventsProps {
    IndividualId: number | undefined;
    cardId: number | undefined;
}

interface UsersState {
    cardEvents: CardEvent[];
    globalFilterValue: string;
    isLoading: boolean;
    totalRecords: number;
}

export default class CardEventsComponent extends Component<
    ClientEventsProps,
    UsersState
> {
    private toast: Toast | null = null;
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: ClientEventsProps) {
        super(props);

        this.state = {
            cardEvents: [],
            globalFilterValue: "",
            isLoading: false,
            totalRecords: 0,
        };
    }

    loadEventsData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });

        searchRequest.filters = utils.manageDataTableFilters(
            searchRequest.filters
        );

        const events = await issuingApiService.getCardEvents({
            ...searchRequest,
            sortings: [],
            individualId: this.props.IndividualId,
            cardId: this.props.cardId,
        });

        if (!events.success) {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: events.message,
                life: 3000,
            });
        }

        this.setState({
            cardEvents: events.data,
            totalRecords: events.totalCount,
            isLoading: false,
        });
    };

    formatRawJson = (value?: string | null): string => {
        if (!value) {
            return "";
        }

        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
            return value;
        }
    };

    copyToClipboard = async (value?: string | null) => {
        if (!value) {
            return;
        }

        try {
            await navigator.clipboard.writeText(value);

            this.toast?.show({
                severity: "success",
                summary: "Copied",
                detail: "Event data copied to clipboard",
                life: 2000,
            });
        } catch {
            this.toast?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to copy event data",
                life: 3000,
            });
        }
    };

    renderRawData = (value?: string | null) => {
        if (!value) {
            return null;
        }

        const formattedValue = this.formatRawJson(value);

        return (
            <div
                style={{
                    position: "relative",
                    maxWidth: "500px",
                }}
            >
                <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    style={{
                        position: "absolute",
                        top: "5px",
                        right: "15px",
                        zIndex: 1,
                    }}
                    onClick={() => this.copyToClipboard(value)}
                    title="Copy full value"
                >
                    Copy
                </button>

                <pre
                    style={{
                        margin: 0,
                        padding: "10px",
                        paddingRight: "60px",
                        maxHeight: "200px",
                        overflow: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        border: "1px solid #dee2e6",
                        borderRadius: "4px",
                        fontSize: "12px",
                    }}
                >
                    {formattedValue}
                </pre>
            </div>
        );
    };

    render() {
        return (
            <Fragment>
                <TransitionGroup
                    appear={true}
                    exit={false}
                    enter={false}
                >
                    <CSSTransition
                        classNames="TabsAnimation"
                        timeout={3500}
                    >
                        <Fragment>
                            <div>
                                <Toast
                                    ref={(el) => (this.toast = el)}
                                />

                                <AppCard>
                                    <AppCard.Body>
                                        <Fieldset
                                            legend="Card Events"
                                            toggleable={true}
                                            collapsed={false}
                                        >
                                            <ExtendedDataTable
                                                ref={(ref) =>
                                                    (this.dataTable = ref)
                                                }
                                                value={this.state.cardEvents}
                                                onChange={this.loadEventsData}
                                                loading={this.state.isLoading}
                                                totalRecords={
                                                    this.state.totalRecords
                                                }
                                                defaultSortField="createdTimeStamp"
                                                defaultSortOrder={
                                                    SortOrderType.Descending
                                                }
                                            >
                                                <Column
                                                    {...({
                                                        alwaysVisible: true,
                                                    } as any)}
                                                    field="id"
                                                    header="Client Id"
                                                    body={(
                                                        rowData: CardEvent
                                                    ) =>
                                                        rowData.icardClientId
                                                    }
                                                />

                                                <Column
                                                    field="event"
                                                    header="Event"
                                                    {...({
                                                        alwaysVisible: true,
                                                    } as any)}
                                                    body={(
                                                        rowData: CardEvent
                                                    ) =>
                                                        rowData.event
                                                    }
                                                    filterMatchModeOptions={
                                                        stringFilterMatchModeOptions
                                                    }
                                                />

                                                <Column
                                                    field="eventDt"
                                                    header="Event Date"
                                                    {...({
                                                        alwaysVisible: true,
                                                    } as any)}
                                                    body={(
                                                        rowData: CardEvent
                                                    ) => (
                                                        <p>
                                                            {moment
                                                                .utc(
                                                                    rowData.eventDt
                                                                )
                                                                .local()
                                                                .format(
                                                                    "DD/MM/YYYY HH:mm:ss"
                                                                )}
                                                        </p>
                                                    )}
                                                    filterMatchModeOptions={
                                                        numberFilterMatchModeOptions
                                                    }
                                                    dataType="date"
                                                    filterElement={
                                                        dateFilterTemplate
                                                    }
                                                />

                                                <Column
                                                    field="requestRaw"
                                                    header="Event Request"
                                                    {...({
                                                        alwaysVisible: false,
                                                        defaultHidden: true
                                                    } as any)}
                                                    body={(
                                                        rowData: CardEvent
                                                    ) =>
                                                        this.renderRawData(
                                                            rowData.requestRaw
                                                        )
                                                    }
                                                />

                                                <Column
                                                    field="responseRaw"
                                                    header="Event Response"
                                                    {...({
                                                        alwaysVisible: false,
                                                        defaultHidden: true
                                                    } as any)}
                                                    body={(
                                                        rowData: CardEvent
                                                    ) =>
                                                        this.renderRawData(
                                                            rowData.responseRaw
                                                        )
                                                    }
                                                />
                                            </ExtendedDataTable>
                                        </Fieldset>
                                    </AppCard.Body>
                                </AppCard>
                            </div>
                        </Fragment>
                    </CSSTransition>
                </TransitionGroup>
            </Fragment>
        );
    }
}
